import axios from "axios";
import React, { useContext, useState } from "react";

import {
  CheckInRoundOption,
  Group,
  Round,
  Sponsor,
  TicketCategory,
  Tournament,
} from "../../types";
import { notify } from "../../utils/notify";
import {
  getAllTournaments,
  getSponsorsByTicketCategory,
  getTournamentAmountRaised,
  getTournamentById,
  getTournamentRounds,
  getTournamentTicketCategories,
} from "../httpClient";

export interface TournamentStateContextInterface {
  tournamentIdState: number;
  setTournamentIdState: React.Dispatch<React.SetStateAction<number>>;
  tournamentState: Tournament;
  amountRaisedState: number;
  getAmountRaised: (id: number) => Promise<number>;
  setTournamentById: (id: number) => Promise<any>;
  getMostRecentLiveTournament: () => Promise<Tournament>;
  roundsState: Round[];
  getRoundsState: (tournamentId: number, groups?: Group[]) => Promise<any>;
  setRoundsState: (value: React.SetStateAction<Round[]>) => void;
  selectedCheckInRound: CheckInRoundOption;
  setSelectedCheckInRound: React.Dispatch<
    React.SetStateAction<CheckInRoundOption>
  >;
  //Ticket Categories
  ticketCategoriesState: TicketCategory[];
  getTicketCategoriesState: (
    tournamentId: number,
    isSponsor?: boolean
  ) => Promise<any>;
  setTicketCategoriesState: React.Dispatch<
    React.SetStateAction<TicketCategory[]>
  >;
  // Sponsors
  sponsorsState: Sponsor[];
  getSponsorsState: (ticketCategoryId: number) => Promise<void>;
}

export const TournamentStateContext =
  React.createContext<TournamentStateContextInterface | null>(null);

export const TournamentStateProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [tournamentIdState, setTournamentIdState] = useState<number>(0);
  const [tournamentState, setTournamentState] = useState<Tournament>({
    id: 0,
    name: "",
    description: "",
    liveFlag: false,
    dateStart: "",
    dateEnd: "",
    locationTitle: "",
    locationAddress: "",
    bannerPhotoFileName: "",
    bannerPhotoContentType: "",
    bannerPhotoFileSize: 0,
    bannerPhotoUpdatedAt: null,
    bannerPhotoUrl: null,
    slug: "",
    createdAt: "",
    updatedAt: "",
    rounds: [],
    logos: [],
  });
  const [amountRaisedState, setAmountRaisedState] = useState<number>(0);
  const [roundsState, setRoundsState] = useState<Round[]>([]);
  const [selectedCheckInRound, setSelectedCheckInRound] =
    useState<CheckInRoundOption>({
      id: 0,
      name: "All Rounds",
    });
  const [ticketCategoriesState, setTicketCategoriesState] = useState<
    TicketCategory[]
  >([]);
  const [sponsorsState, setSponsorsState] = useState<Sponsor[]>([]);

  // Might need to use useCallback for each get below

  async function setTournamentById(id: number) {
    const result = await getTournamentById(id);
    if (!result) {
      throw new Error("couldn't find tournament");
    }
    setTournamentState(result);
    setTournamentIdState(result.id);
  }

  async function getMostRecentLiveTournament() {
    const tournaments: Tournament[] = await getAllTournaments();
    if (!tournaments || tournaments.length === 0) {
      throw new Error("couldn't find most recent live tournament");
    }
    // Sort live tournaments by dateStart in descending order
    const sortedTournaments = tournaments
      .filter((t) => t.liveFlag)
      .sort(
        (a: Tournament, b: Tournament) =>
          new Date(b.dateStart).getTime() - new Date(a.dateStart).getTime()
      );
    return sortedTournaments[0];
  }

  async function getAmountRaised(id: number) {
    const result = await getTournamentAmountRaised(id);
    setAmountRaisedState(result.totalAmountRaised);
    return result.totalAmountRaised;
  }

  // rounds associated with the selected tournament
  const getRoundsState = async (
    tournamentId: number,
    ticketGroups?: Group[]
  ) => {
    const tournamentRounds = await getTournamentRounds(tournamentId);
    tournamentRounds.forEach((r: Round) => (r.numUserGroupAssigned = 0));
    tournamentRounds.forEach((r: Round, i: number) => {
      ticketGroups?.forEach((g: Group) => {
        if (r.id === g?.roundId) {
          tournamentRounds[i].numUserGroupAssigned += 1;
        }
      });
    });
    setRoundsState(tournamentRounds);
  };

  const getTicketCategoriesState = async (
    tournamentId: number,
    isSponsor?: boolean
  ) => {
    try {
      // API returns only the ticket categories that the user is authorized to see (internal categories are filtered out for non-admins)
      const ticketCategories = await getTournamentTicketCategories(
        tournamentId,
        isSponsor
      );
      setTicketCategoriesState(ticketCategories);
    } catch (error) {
      // axios errors already print to the console and alert the user of error
      if (!axios.isAxiosError(error)) {
        console.error("Error fetching ticket categories:", error);
        notify.error("An error occurred while fetching ticket categories.");
      }
    }
  };

  const getSponsorsState = async (ticketCategoryId: number) => {
    const sponsorsData = await getSponsorsByTicketCategory(ticketCategoryId);
    setSponsorsState(sponsorsData);
  };

  return (
    <TournamentStateContext.Provider
      value={{
        tournamentIdState,
        setTournamentIdState,
        tournamentState,
        amountRaisedState,
        setTournamentById,
        getMostRecentLiveTournament,
        getAmountRaised,
        roundsState,
        getRoundsState,
        setRoundsState,
        selectedCheckInRound,
        setSelectedCheckInRound,
        ticketCategoriesState,
        getTicketCategoriesState,
        setTicketCategoriesState,
        sponsorsState,
        getSponsorsState,
      }}
    >
      {children}
    </TournamentStateContext.Provider>
  );
};

export const useTournamentStateContext = () => {
  const context = useContext(TournamentStateContext);
  if (!context) {
    throw new Error(
      "TournamentStateContext must be used inside TournamentStateProvider"
    );
  }
  return context;
};
